import { getImageSize, pixelToDxa } from '@/libs/utils/docxUtils'
import {
  AlignmentType,
  PageBreak,
  Paragraph,
  Table,
  TableCell,
  TableRow,
  ImageRun,
  TextRun,
  VerticalAlign,
  WidthType,
  HeadingLevel,
  TableLayoutType,
} from 'docx'
import SectionHeader from '../../SectionHeader'
import { tableWidthDxa1150 } from '../../constants'

const Cell = ({ children, text = '', textProps = {}, ...props }) =>
  new TableCell({
    margins: { top: 100, bottom: 200, left: 100, right: 100 },
    verticalAlign: VerticalAlign.CENTER,
    children: [
      new Paragraph({
        style: 'textParagraph',
        children: Array.isArray(children) ? children : [new TextRun({ text, ...textProps })], // Ensure children is an array
        alignment: AlignmentType.CENTER,
      }),
    ],
    ...props,
  })

const HeaderCell = ({ textProps, ...props }) =>
  Cell({
    textProps: { ...textProps, bold: true },
    ...props,
  })

const Chapter4Sub4Page = async ({ barChart4Img, reportData }) => {
  const { deptReportGroups } = reportData || {}

  const { width, height } = await getImageSize({ image: barChart4Img, width: 550 })

  const count = deptReportGroups.filter(item => item.havePhish === true).length

  // const limitedData = deptReportGroups.slice(0, 5) || []

  const formattedData =
    Array.isArray(deptReportGroups) && deptReportGroups.length > 0
      ? deptReportGroups.map(data => {
          const formatPercent = value => {
            // 檢查是否存在且為數字，並且小數點後超過 2 位才進行處理
            if (value != null) {
              const strValue = value.toString()
              const decimalPart = strValue.split('.')[1] // 小數部分
              return decimalPart && decimalPart.length > 2 ? value.toFixed(2) : value
            }
            return '0.00' // 如果數值不存在或無效，回傳 '0.00'
          }

          // 確保所有的報告數據都有默認值，避免 null 或 undefined
          const formattedReport = {
            userCount: data.report.userCount || 0,
            openCount: data.report.openCount || 0,
            openPercent: formatPercent(data.report.openPercent) ?? 0,
            linkCount: data.report.linkCount || 0,
            linkPercent: formatPercent(data.report.linkPercent) ?? 0,
            fileCount: data.report.fileCount || 0,
            filePercent: formatPercent(data.report.filePercent) ?? 0,
            textCount: data.report.textCount || 0,
            textPercent: formatPercent(data.report.textPercent) ?? 0,
          }

          return new TableRow({
            children: [
              new Cell({
                children: [
                  new TextRun({
                    text: `${data.groupId}`,

                    font: { name: 'Microsoft JhengHei', eastAsia: 'Microsoft JhengHei' },
                    size: 20,
                  }),
                ],
                shading: { fill: '#e8ecf2' },
              }),
              new Cell({
                children: [
                  new TextRun({
                    text: `${data.groupName}`,

                    font: { name: 'Microsoft JhengHei', eastAsia: 'Microsoft JhengHei' },
                    size: 20,
                  }),
                ],
                shading: { fill: '#e8ecf2' },
              }),
              new Cell({
                children: [
                  new TextRun({
                    text: `${formattedReport.userCount}`,

                    font: { name: 'Microsoft JhengHei', eastAsia: 'Microsoft JhengHei' },
                    size: 20,
                  }),
                ],
                shading: { fill: '#e8ecf2' },
              }),
              new Cell({
                children: [
                  new TextRun({
                    text: `${data.phishCount}`,

                    font: { name: 'Microsoft JhengHei', eastAsia: 'Microsoft JhengHei' },
                    size: 20,
                  }),
                ],
                shading: { fill: '#e8ecf2' },
              }),
              new Cell({
                children: [
                  new TextRun({
                    text: `${formattedReport.openCount}`,

                    font: { name: 'Microsoft JhengHei', eastAsia: 'Microsoft JhengHei' },
                    size: 20,
                  }),
                ],
                shading: { fill: '#e8ecf2' },
              }),
              new Cell({
                children: [
                  new TextRun({
                    text: `${formattedReport.linkCount}`,

                    font: { name: 'Microsoft JhengHei', eastAsia: 'Microsoft JhengHei' },
                    size: 20,
                  }),
                ],
                shading: { fill: '#e8ecf2' },
              }),
              new Cell({
                children: [
                  new TextRun({
                    text: `${formattedReport.fileCount}`,

                    font: { name: 'Microsoft JhengHei', eastAsia: 'Microsoft JhengHei' },
                    size: 20,
                  }),
                ],
                shading: { fill: '#e8ecf2' },
              }),
              new Cell({
                children: [
                  new TextRun({
                    text: `${formattedReport.textCount}`,

                    font: { name: 'Microsoft JhengHei', eastAsia: 'Microsoft JhengHei' },
                    size: 20,
                  }),
                ],
                shading: { fill: '#e8ecf2' },
              }),
            ],
          })
        })
      : []

  const tableRows =
    deptReportGroups.length > 0
      ? formattedData // 如果有數據，則顯示實際數據行
      : [
          new TableRow({
            children: Array(8).fill(
              // 用 8 個單元格填充空數據
              new Cell({
                children: [
                  new TextRun({
                    text: '-',

                    font: { name: 'Microsoft JhengHei', eastAsia: 'Microsoft JhengHei' },
                    size: 20,
                  }),
                ],
                shading: { fill: '#e8ecf2' },
              }),
            ),
          }),
        ]

  return [
    new Paragraph({ children: [new PageBreak()] }),
    new Paragraph(''),
    SectionHeader({
      text: '4.3 部門結果統計報表',
      size: 48,
      headingNumber: HeadingLevel.HEADING_2,
    }),
    new Paragraph(''),
    new Paragraph({
      style: 'textParagraph',
      children: [
        new TextRun({
          text: '\t',
          font: { name: 'Microsoft JhengHei', eastAsia: 'Microsoft JhengHei' },
          size: 24,
        }),
        new TextRun({
          text: '本次演練僅有',
          font: { name: 'Microsoft JhengHei', eastAsia: 'Microsoft JhengHei' },
          size: 24,
        }),
        new TextRun({
          text: `${count}`,
          font: { name: 'Microsoft JhengHei', eastAsia: 'Microsoft JhengHei' },
          size: 24,
        }),
        new TextRun({
          text: '個部門同仁被釣中，依照',
          font: { name: 'Microsoft JhengHei', eastAsia: 'Microsoft JhengHei' },
          size: 24,
        }),
        new TextRun({
          text: '各部門參與演練與被釣中人數',
          bold: true,
          font: { name: 'Microsoft JhengHei', eastAsia: 'Microsoft JhengHei' },
          size: 24,
        }),
        new TextRun({
          text: '進行統計，統計圖表如下顯示:',
          font: { name: 'Microsoft JhengHei', eastAsia: 'Microsoft JhengHei' },
          size: 24,
        }),
        ...(count > 10
          ? [
              new TextRun({
                text: '(此圖表僅列出前十名的部門資訊)',
                font: { name: 'Microsoft JhengHei', eastAsia: 'Microsoft JhengHei' },
                size: 24,
              }),
            ]
          : []),
      ],
    }),
    new Paragraph(''),
    new Paragraph(''),
    new Paragraph({
      alignment: AlignmentType.CENTER,
      // spacing: { before: pixelToDxa(30), after: pixelToDxa(30) },
      children: [
        new ImageRun({
          data: barChart4Img,
          transformation: {
            width,
            height,
          },
        }),
      ],
    }),
    new Paragraph({ children: [new PageBreak()] }),
    new Paragraph(''),
    SectionHeader({
      text: '4.3.1部門統計結果清單',
      size: 40,
      headingNumber: HeadingLevel.HEADING_3,
    }),
    new Paragraph(''),
    new Paragraph({
      style: 'textParagraph',
      children: [
        new TextRun({
          text: '\t',
          font: { name: 'Microsoft JhengHei', eastAsia: 'Microsoft JhengHei' },
          size: 24,
        }),
        new TextRun({
          text: '依照',
          font: { name: 'Microsoft JhengHei', eastAsia: 'Microsoft JhengHei' },
          size: 24,
        }),
        new TextRun({
          text: '各部門受測人數',
          font: { name: 'Microsoft JhengHei', eastAsia: 'Microsoft JhengHei' },
          size: 24,
        }),
        new TextRun({
          text: '，統計出',
          font: { name: 'Microsoft JhengHei', eastAsia: 'Microsoft JhengHei' },
          size: 24,
        }),
        new TextRun({
          text: '各部門之受測者',
          font: { name: 'Microsoft JhengHei', eastAsia: 'Microsoft JhengHei' },
          size: 24,
        }),
        new TextRun({
          text: '「瀏覽郵件」、「點閱連結」、「開啟附件」與「輸入資料」',
          font: { name: 'Microsoft JhengHei', eastAsia: 'Microsoft JhengHei' },
          size: 24,
        }),
        new TextRun({
          text: '等之人數。',
          font: { name: 'Microsoft JhengHei', eastAsia: 'Microsoft JhengHei' },
          size: 24,
        }),
      ],
    }),
    new Paragraph(''),
    new Paragraph(''),
    new Table({
      indent: { size: pixelToDxa(-50), type: WidthType.DXA },
      layout: TableLayoutType.FIXED,
      // columnWidths: Array(8).fill(tableWidthDxa1150 / 8), // 自動填充 8 個等寬欄位
      columnWidths: [0.05, 0.35, 0.1, 0.1, 0.1, 0.1, 0.1, 0.1].map(
        ratio => tableWidthDxa1150 * ratio,
      ),
      width: { size: tableWidthDxa1150, type: WidthType.DXA },
      rows: [
        // 第一行 - 表頭，顯示受測人數以及每個分類的總稱
        new TableRow({
          children: [
            HeaderCell({
              children: [
                new TextRun({
                  text: '排名',
                  bold: true,
                  color: '#ffffff',
                  font: { name: 'Microsoft JhengHei', eastAsia: 'Microsoft JhengHei' },
                  size: 20,
                }),
              ],
              shading: { fill: '#4c82bf' },
            }),
            HeaderCell({
              children: [
                new TextRun({
                  text: '部門',
                  bold: true,
                  color: '#ffffff',
                  font: { name: 'Microsoft JhengHei', eastAsia: 'Microsoft JhengHei' },
                  size: 20,
                }),
              ],
              shading: { fill: '#4c82bf' },
            }),
            HeaderCell({
              children: [
                new TextRun({
                  text: '各部門受測人數',
                  bold: true,
                  color: '#ffffff',
                  font: { name: 'Microsoft JhengHei', eastAsia: 'Microsoft JhengHei' },
                  size: 20,
                }),
              ],
              shading: { fill: '#4c82bf' },
            }),
            HeaderCell({
              children: [
                new TextRun({
                  text: '各部門被釣中人數',
                  bold: true,
                  color: '#ffffff',
                  font: { name: 'Microsoft JhengHei', eastAsia: 'Microsoft JhengHei' },
                  size: 20,
                }),
              ],
              shading: { fill: '#4c82bf' },
            }),
            HeaderCell({
              children: [
                new TextRun({
                  text: '瀏覽信件人數',
                  bold: true,
                  color: '#ffffff',
                  font: { name: 'Microsoft JhengHei', eastAsia: 'Microsoft JhengHei' },
                  size: 20,
                }),
              ],
              shading: { fill: '#4c82bf' },
            }),
            HeaderCell({
              children: [
                new TextRun({
                  text: '點閱連結人數',
                  bold: true,
                  color: '#ffffff',
                  font: { name: 'Microsoft JhengHei', eastAsia: 'Microsoft JhengHei' },
                  size: 20,
                }),
              ],
              shading: { fill: '#4c82bf' },
            }),
            HeaderCell({
              children: [
                new TextRun({
                  text: '開啟附件人數',
                  bold: true,
                  color: '#ffffff',
                  font: { name: 'Microsoft JhengHei', eastAsia: 'Microsoft JhengHei' },
                  size: 20,
                }),
              ],
              shading: { fill: '#4c82bf' },
            }),
            HeaderCell({
              children: [
                new TextRun({
                  text: '輸入資料人數',
                  bold: true,
                  color: '#ffffff',
                  font: { name: 'Microsoft JhengHei', eastAsia: 'Microsoft JhengHei' },
                  size: 20,
                }),
              ],
              shading: { fill: '#4c82bf' },
            }),
          ],
        }),
        // 數據行，顯示對應的數據
        ...tableRows,
      ],
    }),
    // new Paragraph(''),
    // new Paragraph({
    //   style: 'textParagraph',
    //   alignment: AlignmentType.RIGHT,
    //   children: [new TextRun({ text: '單位：人', font: { name: 'Microsoft JhengHei', eastAsia: 'Microsoft JhengHei' }, size: 24 })],
    // }),
  ]
}

export default Chapter4Sub4Page
